import site from 'pro/config/site';
import { getParamInSearch } from 'ava-util/dist/request';
import { transJsonKeys } from 'ava-util/dist/data';
import { DEBUG_TRACKER } from 'base/config/constant';
import { IStartTrackDataFull } from 'mod/track/interface';

declare const window: any;

export type IGaProduct = {
    id?: string; //产品 ID 或 SKU（例如 P67890）。对于 id 和 name，必须设置其中之一
    name?: string; //产品名称（例如 Android T 恤）。对于 id 和 name，必须设置其中之一
    brand?: string; //与产品关联的品牌（例如 Google）
    category?: string; //产品所属的类别（例如服装）。可以使用 / 作为分隔符来指定最多 5 个层级（例如服装/男装/T 恤）
    variant?: string; //产品的细分款式（例如黑色）
    price?: number; //产品的价格（例如 29.20）
    quantity?: number; //产品的数量（例如 2）
    coupon?: string; //与产品关联的优惠券代码（例如 SUMMER_SALE13）
    list_name?: string;
    list_position?: number; //产品在列表或集合中的位置（例如 2）
};

export type IGa4Item = {
    item_id?: string; //产品 ID 或 SKU（例如 P67890）。对于 id 和 name，必须设置其中之一
    item_name?: string; //产品名称（例如 Android T 恤）。对于 id 和 name，必须设置其中之一
    affiliation?: string;
    coupon?: string; //与产品关联的优惠券代码（例如 SUMMER_SALE13）
    currency?: string;
    discount?: number;
    index?: number; //产品在列表或集合中的位置（例如 2）
    item_brand?: string; //与产品关联的品牌（例如 Google）
    item_category?: string; //产品所属的类别（例如服装）。可以使用 / 作为分隔符来指定最多 5 个层级（例如服装/男装/T 恤）
    item_category2?: string; //产品所属的类别（例如服装）。可以使用 / 作为分隔符来指定最多 5 个层级（例如服装/男装/T 恤）
    item_category3?: string; //产品所属的类别（例如服装）。可以使用 / 作为分隔符来指定最多 5 个层级（例如服装/男装/T 恤）
    item_category4?: string; //产品所属的类别（例如服装）。可以使用 / 作为分隔符来指定最多 5 个层级（例如服装/男装/T 恤）
    item_category5?: string; //产品所属的类别（例如服装）。可以使用 / 作为分隔符来指定最多 5 个层级（例如服装/男装/T 恤）
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string; //产品的细分款式（例如黑色）
    price?: number; //产品的价格（例如 29.20）
    quantity?: number; //产品的数量（例如 2）
};

export type IGaEvent = {
    page_code?: string;
    page_path?: string;
    id?: string;
    step?: number;
    affiliation?: string;
    revenue?: number | string;
    shipping?: number | string;
    tax?: number | string;
    coupon?: string | string;
    currency?: string;
    items?: IGaProduct[];
    event_id: string;
};

export type IGa4Event = {
    currency?: string;
    payment_type?: string;
    transaction_id?: string;
    value?: number;
    affiliation?: string;
    coupon?: string;
    shipping?: number;
    tax?: number;
    items?: IGa4Item[];
    item_list_id?: string;
    item_list_name?: string;
    content_type?: string;
    event_id: string;
};


let isStarted = false; //是否开始发送数据
let todoFunctions: Function[] = []; //等待执行的回调
let isFirstPV = true; //Rogue Referral： 第一次发送 pageview 事件保留 referrer，第二次开始清除 referrer

function transform(event: IGaEvent): IGa4Event {
    const _event = transJsonKeys(event, {
        id: 'transaction_id',
        revenue: 'value',
    }, [
        'affiliation',
        'shipping',
        'tax',
        'coupon',
        'currency',
        'event_id',
    ]) as IGa4Event;

    if (event.items) {
        _event.items = event.items?.map(item => {
            return transJsonKeys(item, {
                id: 'item_id',
                name: 'item_name',
                brand: 'item_brand',
                category: 'item_category',
                variant: 'item_variant',
                list_name: 'item_list_name',
                list_position: 'index',
            }, ['price', 'quantity', 'coupon', 'currency']);
        });
    }
    return _event;
}

/**
 * 清除referrer
 */
function removeReferrer() {
    Object.defineProperty(document, 'referrer', {
        value: null,
    });
}

/**
 * 打点
 * @param command config|get|set|event|consent，文档https://developers.google.com/tag-platform/gtagjs/reference
 * @param eventName 事件名称
 * @param data 数据
 */
function track(command: string, eventName: string, data?: any) {
    if (typeof data === 'function') {
        data = data();
    }
    if (getParamInSearch(DEBUG_TRACKER) === '1') {
        console.log(`-----GA ${command}: ${eventName} -----`);
        console.log(data);
    }
    if (window.gtag) {
        return window.gtag(command, eventName, data);
    }
}

/**
 * 运行 todoFunctions 中保存你的等待执行的回调函数
 */
function runCallbacks(): void {
    // 执行待执行的回调
    if (todoFunctions.length && window.gtag) {
        todoFunctions.map(callback => callback());
        todoFunctions = [];
    }
}

/**
 * 发送数据前验证是否符合发送条件，不符合条件的暂时保存在 todoFunctions 数组中
 * @param methodName snowplow api中的 method
 * @param data 要发送的数据
 */
function trackOrDelay(command: string, eventName: string, data: any) {
    // sp.js 加载完成并且主动触发 start 之前，放到数组中等待执行
    if (isStarted && window.gtag) {
        track(command, eventName, data);
    } else {
        todoFunctions.push(function() {
            track(command, eventName, data);
        });
    }
}

export function getGaClientId(): Promise<string> {
    return new Promise((resolve, reject) => {
        let success = false;

        if (window.gtag) {
            window.gtag('get', site.ga.trackingId, 'client_id', (clientId: string) => {
                success = true;
                resolve(clientId);
            });
            setTimeout(() => {
                if (!success) {
                    reject('Google analyze is blocked.');
                }
            }, 200);
        } else {
            reject('Google analyze is uninstalled.');
        }
    })
}

/**
 * 开始发出打点请求
 *
 * GA4 has Enhanced measurement, which will trigger page_view on browser history change by default.
 * It could be closed at GA -> Adminn -> Property -> Data Streams -> Web Stream -> Enhanced measurement -> PageView ->
 *    Show advanced settings -> Page changes based on browser history events
 * As we manually trigger page_view for each page, it is closed.
 * And also set { send_page_view: false } on 'config' event to disable auto page_view.
 * https://support.google.com/analytics/answer/11403294?hl=en
 */
export function gaStartTrack(data: IStartTrackDataFull) {
    const {
        userId,
        email,
        lang,
        country,
        currency,
        userTags,
    } = data;

    const d = {
        send_page_view: false,
        transport_url: site.ga.transportUrl,
        first_party_collection: true,
        selected_language: lang,
        country,
        currency,
        env: process.env.NEXT_PUBLIC_ENV_NAME || 'prod',
        user_tags: userTags,
    };

    if (userId === '0') {
        track('config', site.ga.trackingId, d);
    } else {
        track('config', site.ga.trackingId, {
            ...d,
            user_id: userId,
            user_data: {
                external_id: userId,
                email_address: email,
            }
        });
        track('set', 'user_properties', {
            crm_id: userId,
        });
    }

    if (!isStarted) {
        isStarted = true;
        runCallbacks();
    }
}

export function gaEvent(eventName: string, data: IGaEvent | (() => IGaEvent)) {
    const staticData = {} as Record<string, any>;
    if (!['login', 'sign_up', 'generate_lead'].includes(eventName)) {
        staticData['x-fb-cd-content_type'] = ['view_item', 'view_item_list'].includes(eventName)
            ? 'product_group'
            : 'product'
    }

    if (typeof data === 'function') {
        trackOrDelay('event', eventName, () => ({
            ...staticData,
            ...transform(data()),
        }));
    } else {
        trackOrDelay('event', eventName, {
            ...staticData,
            ...(data ? transform(data) : {}),
        });
    }
}

/**
 * Manually trigger page_view event
 */
export function gaPageView(url: string, eventId: string) {
    if (isFirstPV) {
        isFirstPV = false;
    } else {
        removeReferrer();
    }
    trackOrDelay('event', 'page_view', {
        event_id: eventId,
        page_path: url,
    });
}
